/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import IonSnackbar from '@ion/dls/src/notifications/IonSnackbar';
import Render from '@ion/dls/src/render/Render';
import useCurrentGlobalGroupId from '../../hooks/useCurrentGlobalGroupId';
import {setSelectedDevicesTab} from './state';
import {deviceSecondaryDeviceSelector} from './state-selectors';
import useBulkViewDeviceTabs from './useBulkViewDeviceTabs';
import useDeviceSmartSearch from './useDeviceSmartSearch';

const MAX_VIEW_DEVICES = 10;

/**
 * Handles device tab management and error handling for Sentinel device
 * associations. Listens for secondary device identifier changes and loads
 * device data as needed.
 *
 * ```
 * 
 * <SentinelTabView />
 * 
 * ```
 * 
 */
const SentinelTabView = () => {
  const dispatch          = useDispatch();
  const accountId         = useCurrentGlobalGroupId();
  const {loadDevices}     = useDeviceSmartSearch();
  const [error, setError] = useState(false);
  const identifier        = useSelector(deviceSecondaryDeviceSelector);

  const {
    tabItems,
    handleAddRDeviceTab,
    getMaxDeviceMessage,
    getIdentifierExists,
    getDeviceExistMessage
  } = useBulkViewDeviceTabs();

  const getDeviceFromIdentifiers = useCallback((identifier: string) => {
    try {
      loadDevices({
        includeSubGroups : true,
        searchKeyword    : identifier,
        accountId
      }, {
        onSuccess : (results) => {
          const resultsCount = results?.totalCount;

          if (resultsCount > 0) {
            const deviceObj = results?.devices?.find((device) => device?.esn === identifier);

            if (deviceObj) {
              const deviceId = deviceObj?.id;
              const existTab = getIdentifierExists(deviceId);

              if (!isEmpty(existTab)) {
                dispatch(setSelectedDevicesTab(existTab?.data));
              } else {
                handleAddRDeviceTab(deviceObj);
                setError(false);
              }
            } else {
              setError(true);
            }
          } else {
            setError(true);
          }
        },
        onError : () => {
          setError(true);
        }
      });
    } catch (err) {
      setError(true);
    }
  }, [accountId]);

  const onDismissError = function() {
    setError(false);
  };

  useEffect(() => {
    if (tabItems?.length >= MAX_VIEW_DEVICES) {
      getMaxDeviceMessage();
    } else if (identifier) {
      getDeviceFromIdentifiers(identifier);
    }
  }, [identifier, getDeviceFromIdentifiers]);

  return (
    <Render.If condition={error}>
      <IonSnackbar show={error}
        message={translate('admin-devices:dialogBulkView.noResults')}
        actionLabel={translate('common:dismiss')}
        onAction={onDismissError} />
    </Render.If>
  );
};

export default React.memo(SentinelTabView);
